import CustomSelect from '../../components/core/customSelect'
import text from './text.json'
import {Formik} from 'formik'
import {resourceIntegrationSchema} from '../../utils/validation'
import integrationTypes from '../../data/integrationTypes.json'
import {StatusIoIcon, StatuspageIoIcon} from '../../icons'
import {useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {addNewResourceIntegration, updateResourceIntegration, getComponents} from '../../stores/integration'
import Input from '../../components/core/input'
import integrationStatuses from '../../data/integrationsStatuses.json'
import Textarea from '../../components/core/textarea'
import ButtonLoader from '../../components/core/button/ButtonLoader'
import {SimpleBlueButton} from '../../components/core/button'
import {useParams} from 'react-router-dom'
import SkeletonLoader from '../../components/core/skeletonLoader'
import {trimStringValues} from '../../utils/text'

const typeElement = (value, label, isDisabled, icon) => {
    return {value, label, isDisabled, icon}
}

const types = [
    typeElement(integrationTypes.types.statuspageIo, 'Statuspage', false, <StatuspageIoIcon />),
    typeElement(integrationTypes.types.statusIo, 'Status.io', false, <StatusIoIcon/>)
]

const names = {
    type: 'type',
    integration: 'integration',
    components: 'components',
    name: 'name',
    message: 'message',
    status: 'status'
}

const AddIntegration = ({closePopup, modal, integration, initialIntegrations}) => {
    const dispatch = useDispatch()
    const {id} = useParams()
    const [active, setActive] = useState(null)
    const [integrations, setIntegrations] = useState(initialIntegrations?.map(item => {
        return {
            ...item,
            value: item?.provider_id,
            label: item?.name
        }
    }))
    const [components, setComponents] = useState([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [loader, setLoader] = useState({})
    const isEdit = modal?.isEdit

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const initialValues = {
        type: isEdit ? types?.find(item => item?.value === integration?.type) : null,
        integration: isEdit ? integrations?.find(item => item?.value === integration?.integration) : null,
        components: isEdit ? components?.filter(item => integration?.items?.includes(item?.value)) : [],
        name: isEdit ? integration?.name : '',
        message: isEdit ? integration?.message : null,
        status: isEdit ? integrationStatuses?.find(item => item?.value === integration?.status) : integrationStatuses[0]
    }

    const handleChangeType = async(name, data, setFieldValue, resetForm) => {
        if (setFieldValue) {
            setFieldValue(name, data)
            setFieldValue(names.integration, null)
            setFieldValue(names.components, [])
            setComponents([])
        }

        setLoader({
            integration: true
        })

        setIntegrations(initialIntegrations?.filter(item => item?.type === data?.value)?.map(item => {
            return {
                ...item,
                value: item?.provider_id,
                label: item?.name
            }
        }))
        setLoader({})
    }

    const handleChangeIntegrations = async(name, data, setFieldValue) => {
        setFieldValue && setFieldValue(name, data)
        setLoader({
            components: true
        })
        const res = await dispatch(getComponents(data?.value))
        setComponents(res?.components?.map(item => {
            return {
                ...item,
                value: item?.id,
                label: item?.name
            }
        }))
        setLoader({})
    }

    const addIntegration = async(values, {setFieldError}) => {
        setIsSubmitting(true)
        if (isEdit) {
            const formData = {
                ...values,
                [names.type]: values.type?.value,
                [names.integration]: values.integration?.value,
                items: values.components?.map(item => item?.value),
                [names.status]: values?.status?.value
            }
            const success = await dispatch(updateResourceIntegration(id, integration?.integration, trimStringValues(formData), setFieldError))
            setIsSubmitting(false)
            if (success) {
                closePopup(true)
            }
            return
        }
        const formData = {
            ...values,
            [names.type]: values.type?.value,
            [names.integration]: values.integration?.value,
            items: values.components?.map(item => item?.value),
            [names.status]: values?.status?.value
        }
        const success = await dispatch(addNewResourceIntegration(id, trimStringValues(formData), setFieldError))
        setIsSubmitting(false)
        if (success) {
            closePopup(true)
        }
    }

    const fetchData = async() => {
        if (!integrations?.length) {
            await handleChangeType(null, integration?.type)
            return
        }

        await handleChangeIntegrations(null, initialValues?.integration)
    }
    useEffect(() => {
        isEdit && fetchData()
    }, [isEdit, JSON.stringify(integrations)])

    return (
        <Formik enableReinitialize={true}
            onSubmit={addIntegration}
            validationSchema={resourceIntegrationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            initialValues={initialValues}>
            {({setFieldValue, handleChange, errors, values, handleSubmit, resetForm}) => (
                <form className="modal-body" onSubmit={handleSubmit}>
                    <CustomSelect
                        placeholder={text.type}
                        onChange={(name, data) => handleChangeType(name, data, setFieldValue, resetForm)}
                        infoText={text.type_info}
                        name={names.type}
                        value={values.type}
                        options={types}
                        activeInfo={active === names.type}
                        handleInfo={(state) => handleInfo(names.type, state)}
                        error={errors.type}
                        isDisabled={isEdit}

                    />
                    {values?.type?.value && <>
                        {loader?.integration ? (
                            <SkeletonLoader className="input-skeleton"/>
                        ) : (
                            <div className="d-flex flex-column gap-half">
                                <CustomSelect
                                    placeholder={text.integration}
                                    onChange={(name, data) => handleChangeIntegrations(name, data, setFieldValue)}
                                    infoText={text.integration_info}
                                    name={names.integration}
                                    value={values.integration || values.name}
                                    options={integrations}
                                    activeInfo={active === names.integration}
                                    handleInfo={(state) => handleInfo(names.integration, state)}
                                    error={errors.integration}
                                    isDisabled={isEdit}
                                />
                            </div>
                        )}
                    </>}
                    {loader?.components ? (
                        <SkeletonLoader className="input-skeleton"/>
                    ) : components?.length > 0 && (
                        <CustomSelect
                            isMulti
                            placeholder={text.components}
                            onChange={(name, data) => setFieldValue(name, data)}
                            infoText={text.components_info}
                            name={names.components}
                            value={values.components}
                            options={components}
                            activeInfo={active === names.components}
                            handleInfo={(state) => handleInfo(names.components, state)}
                            error={errors.components}

                        />
                    )}
                    <Input
                        infoText={text.name_info}
                        placeholder={text.name}
                        name={names.name}
                        value={values[names.name]}
                        onChange={handleChange}
                        activeInfo={active === names.name}
                        handleInfo={(state) => handleInfo(names.name, state)}
                        error={errors[names.name]}

                    />
                    <CustomSelect
                        placeholder={text.status}
                        onChange={(name, data) => setFieldValue(name, data)}
                        infoText={text.status_info}
                        name={names.status}
                        value={values.status}
                        options={integrationStatuses}
                        activeInfo={active === names.status}
                        handleInfo={(state) => handleInfo(names.status, state)}
                        error={errors.status}

                    />
                    <Textarea
                        infoText={text.message_info}
                        placeholder={values.message !== null ? text.message : (text.message_placeholder[values.status?.value] || text.message_placeholder.investigating)}
                        name={names.message}
                        value={values[names.message]}
                        onChange={handleChange}
                        activeInfo={active === names.message}
                        handleInfo={(state) => handleInfo(names.message, state)}
                        error={errors[names.message]}
                        onFocus={() => !values.message && setFieldValue(names.message, '')}
                        onBlur={() => !values.message && setFieldValue(names.message, null)  }
                    />
                    <SimpleBlueButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        {isSubmitting && <ButtonLoader/>}
                        {text.submit}
                    </SimpleBlueButton>
                </form>
            )}
        </Formik>
    )
}

export default AddIntegration
